(ns app.front.view.admin.tenant-management.common
  "Shared presentation helpers for the tenant management views."
  (:require [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.municipality.model :as municipality-model]
            [app.front.ui :as ui]
            [core.front.carbon :as cc]
            [core.front.icons :as i]))

(defn tenant-external-modules
  "The `[{:system :module-name}]` of a tenant summary's enabled external
  products, named from the catalogue."
  [catalogue-by-system {:keys [external-modules]}]
  (keep catalogue-by-system external-modules))

(defn products
  "The permit types that are products in their own right: the primary ones, as
  supplemental types (Supplemental Building Permit, Licence follow-up) derive
  from theirs."
  [permit-types]
  (filter ptc-model/primary-permit-types permit-types))

(defn product-name
  "Translated name of a permit type (a \"product\" in the design's language)."
  [tr permit-type]
  (tr [(keyword "permit-type" (name permit-type))]))

(defn product-tag
  "A permit-type pill with its product icon, when it has one. `color` is a
  `ui/tag` colour, default `:white`; the icon follows the text colour."
  ([tr permit-type] (product-tag tr permit-type nil))
  ([tr permit-type color]
   [ui/tag {:type :round :color (or color :white)}
    [:span.flex.items-center.gap-1
     (when-let [icon (ui/permit-type-icon permit-type)]
       ;; Width/height must be strings — some permit-type icons route through
       ;; `cc/icon`, whose schema requires `s/Str` for these.
       [:> icon {:class-name "block shrink-0"
                 :width      "16"
                 :height     "16"}])
     (product-name tr permit-type)]]))

(defn external-module-tag
  "The pill of a product another system implements, named after its module."
  ([module-name] (external-module-tag module-name nil))
  ([module-name color]
   [ui/tag {:type :round :color (or color :white)}
    [:span.flex.items-center.gap-1
     (cc/icon {:icon        i/external-link
               :description ""
               :aria-hidden true
               :width       "16"
               :height      "16"
               :fill        "currentColor"
               :class-name  "block shrink-0"})
     module-name]]))

(defn product-tags
  "A tenant's product pills: its permit types by translated name, then its
  external products.

  Opts:
  - `:color` `ui/tag` colour for the pills (default `:white`)
  - `:class` extra classes for the wrapper, e.g. to change how the pills wrap
  - `:external-modules` `[{:system :module-name}]` of the tenant's external products"
  ([tr permit-types] (product-tags tr permit-types nil))
  ([tr permit-types {:keys [color class external-modules]}]
   (-> [:div.flex.flex-wrap.items-center.-m-1
        (when class {:class class})]
       (into (for [pt (sort-by (partial product-name tr) (products permit-types))]
               ^{:key (str pt)} [product-tag tr pt color]))
       (into (for [{:keys [system module-name]} (sort-by :module-name external-modules)]
               ^{:key (str system)} [external-module-tag module-name color])))))

(defn status
  "Row status for the sortable-table border colour system."
  [{:keys [active?]}]
  (if active? :ok :inactive))

(defn subdivision-name
  "Translated province / state of a tenant, or nil if it has none."
  [tr {:keys [subdivision]}]
  (when subdivision
    (municipality-model/subdivision-name tr subdivision)))

(defn subdivision-flag
  "The province/state flag, or nil when there is no asset for it. Decorative
  (empty alt, aria-hidden): the subdivision name beside it carries the meaning."
  [{:keys [subdivision]}]
  (when-let [flag (ui/subdivision-flag-url subdivision)]
    [:img.shrink-0.border.border-solid.border-ui-gray
     {:src         flag
      :alt         ""
      :aria-hidden true
      :style       {:height "24px" :max-width "48px" :object-fit "contain"}}]))

(defn subdivision-cell
  "Province / state name followed by its flag, for layouts where both share one
  slot (the table gives the flag its own column instead)."
  [tr tenant]
  (when-let [label (subdivision-name tr tenant)]
    [:span.flex.items-center.gap-2
     label
     [subdivision-flag tenant]]))
